import * as _ from 'lodash';
import { Reducer } from 'redux';

import {
  DefinitionState,
  HomeActionType,
  HomeState,
  ReportSidePanelState,
  ReportState,
  SelectedSpace,
} from '~/actions/home-actions';
import { DEFAULT_REPORT_SEARCH_STATE, reportSearchReducer } from '~/reducers/home-reducers/report-search.reducer';

export const DEFAULT_SELECTED_SPACE: SelectedSpace = {
  token: '',
  name: 'recent',
  title: 'Recently Viewed',
  subTitle: 'Recently Viewed',
  space: null,
  preferenceView: 'reports',
  allSpaces: null,
  statusCode: null,
};

export const DEFAULT_REPORT_STATE: ReportState = {
  reportList: [],
  nextPage: null,
  searchOptions: {},
  reportListLoading: true,
  noReports: false,
  hasArchived: false,
  reportsCollection: null,
};

export const DEFAULT_REPORT_SIDE_PANEL_STATE: ReportSidePanelState = {
  reportSidePanelOpen: false,
  reportSidePanelReport: null,
  reportSidePanelQueries: [],
};

export const DEFAULT_DEFINITION_STATE: DefinitionState = {
  definitionList: [],
  definitionListLoading: false,
  nextPage: null,
};

const selectedSpace = (state, action): HomeState => {
  switch (action.type) {
    case HomeActionType.SelectStarred:
      return {
        selectedSpace: {
          token: '',
          name: 'starred',
          title: 'Starred',
          subTitle: 'Starred',
          space: null,
          preferenceView: 'starred',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectMyWork:
      return {
        selectedSpace: {
          token: '',
          name: 'mywork',
          title: 'My Work',
          subTitle: 'My Work',
          space: null,
          preferenceView: 'search',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectDiscover:
      return {
        selectedSpace: {
          token: '',
          name: 'discover',
          title: 'Discover',
          subTitle: 'Discover',
          space: null,
          preferenceView: 'discover',
          allSpaces: null,
          statusCode: null,
        },
        reportState: {
          reportList: [],
          nextPage: null,
          searchOptions: {},
          reportListLoading: false,
          noReports: true,
          hasArchived: false,
          reportsCollection: null,
        },
      };
    case HomeActionType.SelectExplorations:
      return {
        selectedSpace: {
          token: '',
          name: 'myexplorations',
          title: 'My Explorations',
          subTitle: 'My Explorations',
          space: null,
          preferenceView: 'explorations',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectRecent:
      return {
        selectedSpace: {
          token: '',
          name: 'recent',
          title: 'Recently Viewed',
          subTitle: 'Recently Viewed',
          space: null,
          preferenceView: 'reports',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectSpacesIndexPending:
    case HomeActionType.SelectSpacesIndexPaginated:
      return {
        selectedSpace: {
          token: '',
          name: 'spacesIndex',
          title: 'All Collections',
          subTitle: 'All Collections',
          space: null,
          preferenceView: '',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectSpacesIndexFulfilled:
      if (state.selectedSpace && state.selectedSpace.name !== 'spacesIndex') {
        return;
      }
      return {
        selectedSpace: {
          token: '',
          name: 'spacesIndex',
          title: 'All Collections',
          subTitle: 'All Collections',
          space: null,
          preferenceView: '',
          allSpaces: action.payload.spaces.filter((space) => space.openSpace || space.restricted),
          statusCode: action.payload.status,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectSpacesIndexRejected:
    // fallthrough
    case HomeActionType.SelectSpaceRejected:
      return {
        selectedSpace: {
          token: '',
          name: 'space',
          title: '',
          subTitle: '',
          space: null,
          preferenceView: '',
          allSpaces: null,
          statusCode: action.payload.status,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectSpacePending:
      return {
        selectedSpace: {
          token: '',
          name: 'spaceLoading',
          title: '',
          subTitle: '',
          space: null,
          preferenceView: '',
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectSpaceFulfilled:
      return {
        selectedSpace: {
          token: action.payload ? action.payload.token : '',
          name: 'space',
          title: action.payload ? action.payload.name : '',
          subTitle: action.payload ? action.payload.description : '',
          space: action.payload,
          preferenceView: action.payload && action.payload.free_default ? 'organization_space' : '',
          allSpaces: null,
          statusCode: action.payload ? action.payload.status : null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.SelectDefinitions:
      return {
        selectedSpace: {
          token: '',
          name: 'datasource',
          title: 'Datasources',
          subTitle: '',
          space: null,
          preferenceView: state.selectedSpace.preferenceView,
          datasourceToken: action.dataSourceToken,
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };

    case HomeActionType.SelectReportExplore:
      return {
        selectedSpace: {
          token: '',
          name: 'exploreReport',
          title: '',
          subTitle: '',
          space: state.selectedSpace,
          preferenceView: state.selectedSpace.preferenceView,
          datasourceToken: action.dataSourceToken,
          allSpaces: null,
          statusCode: null,
        },
        reportState: DEFAULT_REPORT_STATE,
      };
  }
};

const getSpaces = (state, action): { mySpaces: any[] } => {
  switch (action.type) {
    case HomeActionType.SetSpaces:
      return {
        mySpaces: action.spaces,
      };
    case HomeActionType.GetSpacesFulfilled:
      return {
        mySpaces: action.payload,
      };
    case HomeActionType.InsertSpace:
    case HomeActionType.CreateSpace:
      return {
        mySpaces: _.unionBy(state.mySpaces, [action.space], 'token'),
      };
    case HomeActionType.RemoveSpace:
    case HomeActionType.RenameSpace:
    case HomeActionType.DeleteSpace:
      return {
        mySpaces: state.mySpaces.filter((space) => space.token !== action.space.token),
      };
  }
};

export const getMySpaceKeys = _.memoize((state) => _.keyBy(state, 'token'));

const getSpaceCount = (state, action): { totalSpaceCount: Number } => {
  switch (action.type) {
    case HomeActionType.SetSpaceCount:
      return {
        totalSpaceCount: action.count,
      };
    case HomeActionType.DeleteSpace:
      return {
        totalSpaceCount: Math.max(state.totalSpaceCount - 1, 0),
      };
    case HomeActionType.CreateSpace:
      return {
        totalSpaceCount: state.totalSpaceCount + 1,
      };
  }
};

const getDataSourceCount = (state, action): { totalDataSourceCount: Number } => {
  switch (action.type) {
    case HomeActionType.GetDataSourcesFulfilled:
      return {
        totalDataSourceCount: action.payload,
      };
  }
};

/**
 * This is not a reducer function. Instead is a helper function that
 * inspects the action and determines if an update to the report state
 * is necessary.
 * See: https://github.com/mode/issues/issues/5342
 *
 * @param state The full state object
 * @param action The action being processed
 */
const shouldUpdateReportState = (state, action): boolean => {
  if (action.meta && action.meta.reportListDetails) {
    // If the view has changed since this action was fired, do not update
    if (action.meta.reportListDetails.name !== state.selectedSpace.name) {
      return false;
    }
    // If the view was changed from one space to a different space, do not update
    if (action.meta.reportListDetails.token && action.meta.reportListDetails.token !== state.selectedSpace.token) {
      return false;
    }
  }

  return true;
};

const getReportList = (state, action): { reportState: ReportState } => {
  let report: any;
  switch (action.type) {
    case HomeActionType.GetReportList:
      return {
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.GetReportListPending:
      return {
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.GetReportListFulfilled:
      if (!shouldUpdateReportState(state, action)) {
        return;
      }
      return {
        reportState: {
          reportList: action.payload.reports ? action.payload.reports : action.payload,
          nextPage: action.payload.nextPage,
          searchOptions: action.meta ? action.meta.options : {},
          reportListLoading: false,
          noReports: action.payload.reports ? !action.payload.reports.length : !action.payload.length,
          hasArchived: action.payload.archivedReports || false,
          reportsCollection: action.payload.reportsCollection,
        },
      };
    case HomeActionType.FilterReportList:
      return {
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.FilterReportListPending:
      return {
        reportState: {
          reportList: state.reportState.reportList, // dont flash empty content between filters
          nextPage: state.reportState.nextPage,
          searchOptions: state.reportState.searchOptions,
          reportListLoading: true,
          noReports: false,
          hasArchived: false,
          reportsCollection: null,
        },
      };
    case HomeActionType.FilterReportListFulfilled:
      if (!shouldUpdateReportState(state, action)) {
        return;
      }

      // dirty checking/race condition protection:
      // check if the search state has changed since the pending action was called.
      // if it has then that means another request has interrupted this search which
      // means we should ignore this response and wait for the next result
      const dirty = !_.isEqual(action.meta.options, state.reportSearch.forthcomingSearchOptions);
      return {
        reportState: {
          reportList: dirty ? state.reportState.reportList : action.payload.reports,
          nextPage: dirty ? state.reportState.nextPage : action.payload.nextPage,
          searchOptions: dirty ? state.reportState.searchOptions : action.meta.options,
          reportListLoading: dirty ? true : false,
          noReports: false,
          hasArchived: false,
          reportsCollection: null,
        },
      };
    case HomeActionType.MoreReportList:
      return {
        reportState: DEFAULT_REPORT_STATE,
      };
    case HomeActionType.MoreReportListPending:
      return {
        reportState: {
          reportList: state.reportState.reportList, // dont flash empty content between more calls
          nextPage: state.reportState.nextPage,
          searchOptions: state.reportState.searchOptions,
          reportListLoading: true,
          noReports: false,
          hasArchived: false,
          reportsCollection: state.reportState.reportsCollection,
        },
      };
    case HomeActionType.MoreReportListFulfilled:
      if (!shouldUpdateReportState(state, action)) {
        return;
      }

      return {
        reportState: {
          reportList: _.unionBy(state.reportState.reportList, action.payload.reports, 'token'), // add to existing list
          nextPage: action.payload.nextPage,
          searchOptions: action.meta ? action.meta.options : {},
          reportListLoading: false,
          noReports: false,
          hasArchived: false,
          reportsCollection: action.payload.reportsCollection,
        },
      };
    case HomeActionType.CreateSpaceReportPinFulfilled:
      report = _.find(state.reportState.reportList, (r: any) => r.token === action.payload.report.token);
      if (report) {
        report.savedSpaceReportPin = action.payload.spaceReportPin;
      }
      return {
        reportState: {
          reportList: state.reportState.reportList,
          nextPage: state.reportState.nextPage,
          searchOptions: state.reportState.searchOptions,
          reportListLoading: state.reportState.reportListLoading,
          noReports: state.reportState.noReports,
          hasArchived: state.reportState.hasArchived,
          reportsCollection: state.reportState.reportsCollection,
        },
      };
    case HomeActionType.RemoveSpaceReportPinFulfilled:
      report = _.find(state.reportState.reportList, (r: any) => r.token === action.payload.token);
      if (report) {
        report.savedSpaceReportPin = null;
      }
      return {
        reportState: {
          reportList: state.reportState.reportList,
          nextPage: state.reportState.nextPage,
          searchOptions: state.reportState.searchOptions,
          reportListLoading: state.reportState.reportListLoading,
          noReports: state.reportState.noReports,
          hasArchived: state.reportState.hasArchived,
          reportsCollection: state.reportState.reportsCollection,
        },
      };
    case HomeActionType.RemoveReportFromList:
      return {
        reportState: {
          ...state.reportState,
          reportList: _.filter(state.reportState.reportList, (r) => r.token !== action.token),
        },
      };
    case HomeActionType.InsertReportToList:
      action.report.savedSpaceReportPin = null;
      return {
        reportState: {
          ...state.reportState,
          reportList: [action.report, ...state.reportState.reportList],
        },
      };
  }
};

const getReportListToggle = (state, action): { reportListToggle: 'list' | 'tile' } => {
  switch (action.type) {
    case HomeActionType.SetReportListToggleList:
      return {
        reportListToggle: 'list',
      };
    case HomeActionType.SetReportListToggleTile:
      return {
        reportListToggle: 'tile',
      };
  }
};

const getReportSidePanelToggle = (state, action): { reportSidePanelState: ReportSidePanelState } => {
  switch (action.type) {
    case HomeActionType.OpenReportSidePanel:
      return {
        reportSidePanelState: {
          ...state.reportSidePanelState,
          reportSidePanelOpen: true,
        },
      };
    case HomeActionType.CloseReportSidePanel:
      return {
        reportSidePanelState: {
          ...state.reportSidePanelState,
          reportSidePanelOpen: false,
        },
      };
  }
};

const getReportSidePanelReport = (state, action): { reportSidePanelState: ReportSidePanelState } => {
  switch (action.type) {
    case HomeActionType.SetReportSidePanelReport:
      const _report = state.reportState.reportList.find((report) => report.token === action.token);
      return {
        reportSidePanelState: {
          ...state.reportSidePanelState,
          reportSidePanelReport: _report,
        },
      };
  }
};

const getDefinitionList = (state, action): { definitionState: DefinitionState } => {
  switch (action.type) {
    case HomeActionType.GetDefinitionList:
      return {
        definitionState: {
          definitionList: [],
          definitionListLoading: true,
          nextPage: null,
        },
      };
    case HomeActionType.GetDefinitionListPending:
      return {
        definitionState: {
          definitionList: [],
          definitionListLoading: true,
          nextPage: null,
        },
      };
    case HomeActionType.GetDefinitionListFulfilled:
      return {
        definitionState: {
          definitionList: action.payload.definitions,
          definitionListLoading: false,
          nextPage: action.payload.nextPage,
        },
      };
    case HomeActionType.MoreDefinitionList:
      return {
        definitionState: {
          definitionList: state.definitionState.definitionList,
          definitionListLoading: true,
          nextPage: state.definitionState.nextPage,
        },
      };
    case HomeActionType.MoreDefinitionListPending:
      return {
        definitionState: {
          definitionList: state.definitionState.definitionList, // dont flash empty content between more calls
          definitionListLoading: true,
          nextPage: state.definitionState.nextPage,
        },
      };
    case HomeActionType.MoreDefinitionListFulfilled:
      return {
        definitionState: {
          definitionList: _.unionBy(state.definitionState.definitionList, action.payload.definitions, 'token'), // add to existing list
          definitionListLoading: false,
          nextPage: action.payload.nextPage,
        },
      };
  }
};

export const homeReducerDefaults: HomeState = {
  selectedSpace: DEFAULT_SELECTED_SPACE,
  selectedReport: null,
  reportListToggle: 'tile',
  reportSearch: DEFAULT_REPORT_SEARCH_STATE,
  reportSidePanelState: DEFAULT_REPORT_SIDE_PANEL_STATE,
  reportState: DEFAULT_REPORT_STATE,
  definitionState: DEFAULT_DEFINITION_STATE,
  mySpaces: [],
  totalSpaceCount: 0,
  totalDataSourceCount: 0,
};

export const HomeReducer: Reducer<HomeState> = (state, action) =>
  <HomeState>(
    _.extend(
      {},
      homeReducerDefaults,
      state,
      selectedSpace(state, action),
      getReportList(state, action),
      getReportListToggle(state, action),
      getReportSidePanelToggle(state, action),
      getDefinitionList(state, action),
      getSpaces(state, action),
      getSpaceCount(state, action),
      getDataSourceCount(state, action),
      getReportSidePanelReport(state, action),
      reportSearchReducer(state, action)
    )
  );
